import { type PipelineBoard, type PipelineFilterDefinition, type PipelineOption } from '../types';

export type PipelinePersonRef = {
  id: string;
  name: string | null;
  email?: string | null;
};

export type PipelineNamedRef = {
  id: string;
  name: string | null;
};

type PipelineOptionInput = {
  value: string;
  label: string;
  terminal?: boolean;
};

export const createStaticPipelineFilter = <TFilters extends object = Record<string, unknown>>(
  key: Extract<keyof TFilters, string>,
  urlKey: string,
  label: string,
  options: PipelineOption[],
): PipelineFilterDefinition<TFilters> => ({
  key,
  urlKey,
  label,
  type: 'staticMultiSelect',
  options,
});

export const createBooleanPipelineFilter = <TFilters extends object = Record<string, unknown>>(
  key: Extract<keyof TFilters, string>,
  urlKey: string,
  label: string,
): PipelineFilterDefinition<TFilters> => ({
  key,
  urlKey,
  label,
  type: 'boolean',
});

export const mapPipelineTotals = (totals: {
  entries: number;
  activeEntries: number;
  nonActiveEntries: number;
}): PipelineBoard['totals'] => ({
  cards: totals.entries,
  activeCards: totals.activeEntries,
  nonActiveCards: totals.nonActiveEntries,
});

export const mapPipelineOptions = (options: PipelineOptionInput[]): PipelineOption[] =>
  options.map((option) => ({
    value: option.value,
    label: option.label,
    ...(option.terminal === undefined ? {} : { terminal: option.terminal }),
  }));

export const uniquePipelineOptions = (options: PipelineOptionInput[]): PipelineOption[] => {
  const optionsByValue = new Map<string, PipelineOption>();

  for (const option of options) {
    if (!optionsByValue.has(option.value)) {
      optionsByValue.set(option.value, {
        value: option.value,
        label: option.label,
        ...(option.terminal === undefined ? {} : { terminal: option.terminal }),
      });
    }
  }

  return [...optionsByValue.values()];
};

/**
 * None of these fall back to the identifier. The API resolves names and
 * deliberately soft-fails to null when it cannot, so an id substituted here
 * would render a UUID where a name belongs and read as real data — the bug
 * GP-3954 set out to kill. An unnameable ref is null, and the caller decides
 * what to say in its place.
 */
export const getPipelinePersonLabel = (person: PipelinePersonRef): string | null =>
  firstPresent(person.name, person.email) || null;

export const getPipelineNamedRefLabel = (ref: PipelineNamedRef): string | null =>
  firstPresent(ref.name) || null;

export const getNullablePipelineNamedRefLabel = (ref: PipelineNamedRef | null): string | null =>
  ref ? getPipelineNamedRefLabel(ref) : null;

export const firstPresent = (...values: Array<string | null | undefined>): string => {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }

  return '';
};

export const isPresent = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;
